import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Server, Loader, AlertCircle, ChevronLeft, ChevronRight, Trash2, CheckCircle, RefreshCw } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const PortalLogs = () => {
  const location = useLocation();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 50,
    skip: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, success, failed
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(null);
  const [totalStats, setTotalStats] = useState({ success: 0, failed: 0 });
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // Determine pipeline based on current route
  const isV1Pipeline = location.pathname.startsWith('/v1');
  const pipelineFilter = isV1Pipeline ? 'v1' : 'main';

  const isSuperAdmin = currentUserRole === 'superadmin';

  const fetchLogs = async (skip = 0) => {
    try {
      setLoading(true);
      const limit = 50;
      const response = await apiCall(`/api/logs/portal?skip=${skip}&limit=${limit}&status=${statusFilter}&pipeline=${pipelineFilter}`, { method: 'GET' });

      // Handle new pagination response format
      if (response.data && response.pagination) {
        const transformedLogs = (response.data || []).map(log => ({
          id: log.id,
          timestamp: log.timestamp || log.createdAt,
          requestType: log.requestType,
          endpoint: log.endpoint,
          ipAddress: log.ipAddress,
          statusCode: log.statusCode,
          responseTime: log.responseTime,
          createdBy: log.createdBy || '-'
        }));
        setLogs(transformedLogs);
        setPagination(response.pagination);

        // Update stats if provided
        if (response.stats) {
          setTotalStats(response.stats);
        }
      } else if (Array.isArray(response)) {
        setLogs(response);
      }
    } catch (err) {
      console.error('Error fetching portal logs:', err);
      setError('Failed to load portal logs');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async (days) => {
    try {
      setClearing(true);
      const response = await apiCall('/api/logs/portal/clear', {
        method: 'POST',
        body: JSON.stringify({ days })
      });

      if (response && response.message) {
        setSuccess(`✓ ${response.message}`);
        setConfirmClear(null);
        fetchLogs(0);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response?.error || 'Failed to clear logs');
      }
    } catch (err) {
      console.error('Error clearing logs:', err);
      setError('Failed to clear logs');
    } finally {
      setClearing(false);
    }
  };

  useMountEffect(() => {
    // Get current user role on mount and fetch logs only if authenticated
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      setError('Please login to view logs');
      setLoading(false);
      return;
    }

    const user = JSON.parse(userData);
    setCurrentUserRole(user.role);
    fetchLogs(0);
  });

  useEffect(() => {
    // Only fetch logs if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      fetchLogs(0);
    }
  }, [statusFilter]);

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1><Server size={28} /> {isV1Pipeline ? 'V1 ' : ''}Portal Logs</h1>
          <p>{isV1Pipeline ? 'V1 pipeline f' : 'F'}rontend activity and event logs</p>
        </div>
        <div className="form-section" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spinner" style={{ margin: '0 auto', color: '#3b82f6' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading portal logs...</p>
        </div>
      </div>
    );
  }

  const isSuccess = (status) => status >= 200 && status < 300;
  const isFailed = (status) => status >= 400;

  // Filter logs by search term
  const displayedLogs = logs.filter(log =>
    log.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.requestType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (log.createdBy && log.createdBy.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Server size={28} /> {isV1Pipeline ? 'V1 ' : ''}Portal Logs</h1>
          <p>{isV1Pipeline ? 'V1 pipeline f' : 'F'}rontend activity and event logs</p>
        </div>
        {!isSuperAdmin && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: '#f3f4f6',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            🔒 Read Only
          </div>
        )}
      </div>

      {error && (
        <div className="status-message error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="close-btn">✕</button>
        </div>
      )}

      {success && (
        <div className="status-message success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="close-btn">✕</button>
        </div>
      )}

      <div className="form-section">
        <div className="section-header">
          <h3><Server size={20} /> Portal Events ({pagination.total})</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search endpoint, type, or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                minWidth: '250px'
              }}
            />
            <button className="section-action-btn" onClick={() => fetchLogs(0)} disabled={loading} style={{ background: '#3b82f6', color: 'white', border: 'none' }}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All Logs ({totalStats.success + totalStats.failed})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'success' ? 'active' : ''}`}
            onClick={() => setStatusFilter('success')}
            style={{
              borderColor: totalStats.success > 0 ? '#10b981' : '#e2e8f0',
              color: statusFilter === 'success' ? 'white' : '#10b981'
            }}
          >
            <CheckCircle size={14} /> Success ({totalStats.success})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'failed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('failed')}
            style={{
              borderColor: totalStats.failed > 0 ? '#ef4444' : '#e2e8f0',
              color: statusFilter === 'failed' ? 'white' : '#ef4444'
            }}
          >
            <AlertCircle size={14} /> Failed ({totalStats.failed})
          </button>
        </div>

        {isSuperAdmin && (
          <div style={{ marginBottom: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Clear Old Logs:</span>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 1, label: 'yesterday' })}
              disabled={clearing}
              title="Clear logs from yesterday"
            >
              <Trash2 size={12} />
              <span>Yesterday</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 7, label: 'last 7 days' })}
              disabled={clearing}
              title="Clear logs from last 7 days"
            >
              <Trash2 size={12} />
              <span>7 Days</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 15, label: 'last 15 days' })}
              disabled={clearing}
              title="Clear logs from last 15 days"
            >
              <Trash2 size={12} />
              <span>15 Days</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 30, label: 'last 30 days' })}
              disabled={clearing}
              title="Clear logs from last 30 days"
            >
              <Trash2 size={12} />
              <span>Month</span>
            </button>
          </div>
        )}

        {displayedLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Server size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No logs found</p>
            <p style={{ fontSize: '14px' }}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="collections-table">
              <table>
                <thead>
                  <tr>
                    <th>Created at</th>
                    <th>Request type</th>
                    <th>Endpoint</th>
                    <th>IP Address</th>
                    <th>Status code</th>
                    <th>Response Time (ms)</th>
                    <th>Created by</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedLogs.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td>
                        <span style={{
                          fontWeight: '500',
                          color: log.requestType === 'GET' ? '#3b82f6' :
                                 log.requestType === 'POST' ? '#10b981' :
                                 log.requestType === 'PUT' ? '#f59e0b' :
                                 log.requestType === 'DELETE' ? '#ef4444' :
                                 '#6b7280'
                        }}>
                          {log.requestType || '-'}
                        </span>
                      </td>
                      <td style={{ fontSize: '13px', fontFamily: 'monospace', color: '#1e293b' }}>
                        {log.endpoint || '-'}
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {log.ipAddress || '-'}
                      </td>
                      <td>
                        <span className={`status-badge ${
                          log.statusCode && log.statusCode >= 400 ? 'status-error' :
                          log.statusCode && log.statusCode >= 200 && log.statusCode < 300 ? 'status-success' :
                          'status-info'
                        }`}>
                          {log.statusCode || 'NA'}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {log.responseTime ? `${log.responseTime}ms` : '-'}
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {log.createdBy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
              padding: '16px',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {displayedLogs.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => {
                    if (pagination.skip >= pagination.limit) {
                      fetchLogs(pagination.skip - pagination.limit);
                    }
                  }}
                  disabled={pagination.skip < pagination.limit}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    background: pagination.skip < pagination.limit ? '#e2e8f0' : '#3b82f6',
                    color: pagination.skip < pagination.limit ? '#94a3b8' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pagination.skip < pagination.limit ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  <ChevronLeft size={16} /> Previous
                </button>

                <div style={{ fontSize: '12px', color: '#1e293b', fontWeight: '500', minWidth: '100px', textAlign: 'center' }}>
                  Page {pagination.page} of {pagination.pages}
                </div>

                <button
                  onClick={() => {
                    if (pagination.page < pagination.pages) {
                      fetchLogs(pagination.skip + pagination.limit);
                    }
                  }}
                  disabled={pagination.page >= pagination.pages}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    background: pagination.page >= pagination.pages ? '#e2e8f0' : '#3b82f6',
                    color: pagination.page >= pagination.pages ? '#94a3b8' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {confirmClear && (
        <div className="modal-overlay" onClick={() => setConfirmClear(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>⚠️ Clear Logs</h3>
            <p>
              This will permanently delete all logs from the {confirmClear.label}. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmClear(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleClearLogs(confirmClear.days)}
                disabled={clearing}
              >
                {clearing ? (
                  <>
                    <Loader size={14} className="spinner" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} /> Delete Logs
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalLogs;
